import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { AccountSummary, AuthenticationResult } from '../api/types'
import { accountStorageKey, apiRequest, clearStoredAuthentication, jsonBody, setCSRFToken } from '../api/client'

export function restoreAccount(): AccountSummary | null {
  try {
    const value = sessionStorage.getItem(accountStorageKey)
    return value ? (JSON.parse(value) as AccountSummary) : null
  } catch {
    return null
  }
}

export function hasWorkspaceCapabilities(account: AccountSummary | null): account is AccountSummary {
  return account !== null
    && typeof account.canAccessAdmin === 'boolean'
    && typeof account.canAccessWebmail === 'boolean'
}

export function authenticatedHome(account: AccountSummary): string {
  if (account.canApproveMailConfig) return '/approvals'
  if (account.canAccessAdmin) return '/admin'
  if (account.canAccessWebmail) return '/mail/inbox'
  return '/settings'
}

export function canAccessAuthenticatedPath(account: AccountSummary, path: string): boolean {
  if (path === '/approvals') return account.canApproveMailConfig === true
  if (path === '/admin' || path.startsWith('/admin/')) return account.canAccessAdmin === true
  if (path === '/mail' || path.startsWith('/mail/')) return account.canAccessWebmail === true
  return path === '/settings'
}

export const useSessionStore = defineStore('session', () => {
  const account = ref<AccountSummary | null>(restoreAccount())
  const challenge = ref<string>()
  const challengeAccount = ref<AccountSummary>()
  const challengePurpose = ref<'mfa' | 'mfa_enrollment'>()
  const challengeExpiresAt = ref<string>()
  const enrollmentSecret = ref<string>()
  const enrollmentUri = ref<string>()
  const authenticated = computed(() => account.value !== null)

  function resetChallenge(): void {
    challenge.value = undefined
    challengeAccount.value = undefined
    challengePurpose.value = undefined
    challengeExpiresAt.value = undefined
    enrollmentSecret.value = undefined
    enrollmentUri.value = undefined
  }

  function clearSession(): void {
    account.value = null
    resetChallenge()
    clearStoredAuthentication()
  }

  function accept(result: AuthenticationResult): AuthenticationResult {
    if (result.csrfToken) setCSRFToken(result.csrfToken)
    if (result.next === 'authenticated' || result.next === 'password_change_required') {
      account.value = result.account
      sessionStorage.setItem(accountStorageKey, JSON.stringify(result.account))
      resetChallenge()
    } else {
      challenge.value = result.challenge
      challengeAccount.value = result.account
      challengePurpose.value = result.next
      challengeExpiresAt.value = result.challengeExpiresAt
      enrollmentSecret.value = result.totpSecret
      enrollmentUri.value = result.totpUri
    }
    return result
  }

  async function login(login: string, password: string, deviceName: string): Promise<AuthenticationResult> {
    const result = await apiRequest<AuthenticationResult>('/api/v1/auth/login', {
      method: 'POST',
      body: jsonBody({ login, password, deviceName }),
    })
    return accept(result)
  }

  async function verifyMFA(code: string, deviceName: string): Promise<AuthenticationResult> {
    const result = await apiRequest<AuthenticationResult>('/api/v1/auth/mfa/verify', {
      method: 'POST',
      body: jsonBody({ challenge: challenge.value, code, deviceName }),
    })
    return accept(result)
  }

  async function stepUp(code: string): Promise<void> {
    await apiRequest('/api/v1/auth/mfa/verify', { method: 'POST', body: jsonBody({ code }) })
  }

  async function changePassword(currentPassword: string, newPassword: string, deviceName?: string): Promise<AuthenticationResult> {
    const result = await apiRequest<AuthenticationResult>('/api/v1/me/password', {
      method: 'PUT', body: jsonBody({ currentPassword, newPassword, deviceName }),
    })
    return accept(result)
  }

  async function logout(): Promise<void> {
    try {
      await apiRequest('/api/v1/auth/logout', { method: 'POST', body: jsonBody({}) })
    } finally {
      clearSession()
    }
  }

  async function logoutAll(includeCurrent = true): Promise<void> {
    try {
      await apiRequest('/api/v1/auth/logout-all', { method: 'POST', body: jsonBody({ includeCurrent }) })
    } finally {
      if (includeCurrent) clearSession()
    }
  }

  return {
    account, challenge, challengeAccount, challengePurpose, challengeExpiresAt,
    enrollmentSecret, enrollmentUri, authenticated,
    login, verifyMFA, stepUp, changePassword, logout, logoutAll, resetChallenge, forgetSession: clearSession,
  }
})
